import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import Button from "../Button";
import { Typography } from "../Typography";
import TextField from "../TextField";
import { createCalendarInvite } from "../../Utils/apiServices";
import { useAuth } from "../../Context/authContext";

const daysOfWeek = [
  { key: "sunday", label: "Sunday" },
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
];

const SetAvailabilityModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [availability, setAvailability] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [numberOfWeeks, setNumberOfWeeks] = useState(1);

  // Initialize availability state for all days
  useEffect(() => {
    if (isOpen) {
      const initialAvailability = {};
      daysOfWeek.forEach((day) => {
        initialAvailability[day.key] = {
          enabled: false,
          from: "",
          to: "",
        };
      });
      setAvailability(initialAvailability);
      setError("");
      setNumberOfWeeks(1);
    }
  }, [isOpen]);

  const handleToggleDay = (dayKey) => {
    setAvailability((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        enabled: !prev[dayKey]?.enabled,
      },
    }));
  };

  const handleTimeChange = (dayKey, field, value) => {
    setAvailability((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        [field]: value,
      },
    }));
  };

  const validateAvailability = () => {
    // Check if number of weeks is selected
    if (!numberOfWeeks || numberOfWeeks < 1 || numberOfWeeks > 8) {
      setError("Please select the number of weeks (1-8)");
      return false;
    }

    // Check if at least one day is enabled
    const hasEnabledDay = daysOfWeek.some((day) => availability[day.key]?.enabled);
    if (!hasEnabledDay) {
      setError("Please enable at least one day for availability");
      return false;
    }

    for (const day of daysOfWeek) {
      const dayData = availability[day.key];
      if (dayData?.enabled) {
        if (!dayData.from || !dayData.to) {
          setError(`Please set both From and To times for ${day.label}`);
          return false;
        }
        if (dayData.from >= dayData.to) {
          setError(`From time must be before To time for ${day.label}`);
          return false;
        }
      }
    }
    return true;
  };

  // Generate dates array for calendar invite
  const generateDatesArray = () => {
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day

    // Map day keys to day numbers (0 = Sunday, 1 = Monday, etc.)
    const dayNumberMap = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };

    const totalDays = numberOfWeeks * 7;

    // Generate dates starting from today for the specified number of weeks
    for (let dayOffset = 0; dayOffset < totalDays; dayOffset++) {
      const checkDate = new Date(today);
      checkDate.setDate(today.getDate() + dayOffset);
      const checkDayOfWeek = checkDate.getDay();

      // Check if this day matches any selected day
      for (const day of daysOfWeek) {
        const dayData = availability[day.key];
        if (dayData?.enabled && dayData.from && dayData.to) {
          const targetDay = dayNumberMap[day.key];
          
          // If this date matches the target day, add it
          if (checkDayOfWeek === targetDay) {
            // Parse time strings (HH:mm format)
            const [fromHours, fromMinutes] = dayData.from.split(':').map(Number);
            const [toHours, toMinutes] = dayData.to.split(':').map(Number);

            // Create start and end datetime objects in local time
            const startDateTime = new Date(checkDate);
            startDateTime.setHours(fromHours, fromMinutes, 0, 0);

            const endDateTime = new Date(checkDate);
            endDateTime.setHours(toHours, toMinutes, 0, 0);

            // Convert to ISO string (UTC)
            dates.push({
              start: startDateTime.toISOString(),
              end: endDateTime.toISOString(),
            });
            break; // Only add each date once
          }
        }
      }
    }

    // Sort dates by start time
    dates.sort((a, b) => new Date(a.start) - new Date(b.start));

    return dates;
  };

  const handleSave = async () => {
    if (!user?.id || !user?.email) {
      setError("User not found. Please log in again.");
      return;
    }

    if (!validateAvailability()) {
      return;
    }

    setSaving(true);
    setError("");

    try {
      // Generate calendar invites
      const dates = generateDatesArray();
      
      if (dates.length === 0) {
        setError("No valid dates generated. Please check your availability settings.");
        setSaving(false);
        return;
      }

      const calendarInviteData = {
        title: "Mentor Availability For IgniteNext",
        email: user.email,
        dates: dates,
        description: `Mentor availability for ${numberOfWeeks} week(s) for IgniteNext`,
      };

      const response = await createCalendarInvite(calendarInviteData);

      if (response.success) {
        onClose();
      } else {
        setError(response.error || "Failed to create calendar invites. Please try again.");
      }
    } catch (err) {
      setError(err.error || "Failed to create calendar invites. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 inline-flex items-center justify-center rounded-lg bg-purple-100">
              <div className="h-1 w-6 bg-purple-600 rounded"></div>
            </div>
            <div>
              <Typography as="h2" className="text-xl font-bold text-primary-900">
                Set Availability
              </Typography>
              <Typography as="p" className="text-sm text-tertiary-600 mt-1">
                Please fill in the following details.
              </Typography>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <Icon icon="lucide:x" className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex flex-col gap-4">
              {/* Weeks Selection */}
              <div className="flex flex-col gap-2">
                <label htmlFor="weeks" className="text-sm font-medium text-slate-700 text-left">
                  Set weekly availability
                </label>
                <select
                  id="weeks"
                  name="weeks"
                  value={numberOfWeeks}
                  onChange={(e) => setNumberOfWeeks(Number(e.target.value))}
                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((week) => (
                    <option key={week} value={week}>
                      {week} {week === 1 ? 'week' : 'weeks'}
                    </option>
                  ))}
                </select>
              </div>

              {daysOfWeek.map((day) => {
                const dayData = availability[day.key] || { enabled: false, from: "", to: "" };
                return (
                  <div
                    key={day.key}
                    className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 border border-slate-200 rounded-lg"
                  >
                    {/* Toggle and Day Label */}
                    <div className="flex items-center gap-3 min-w-[140px]">
                      <button
                        type="button"
                        onClick={() => handleToggleDay(day.key)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                          dayData.enabled ? "bg-purple-600" : "bg-slate-300"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            dayData.enabled ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                      <Typography as="label" className="text-sm font-medium text-primary-900">
                        {day.label}
                      </Typography>
                    </div>

                    {/* Time Inputs */}
                    {dayData.enabled && (
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <TextField
                          label="From *"
                          name={`${day.key}_from`}
                          type="time"
                          value={dayData.from}
                          onChange={(e) => handleTimeChange(day.key, "from", e.target.value)}
                          required
                        />
                        <TextField
                          label="To *"
                          name={`${day.key}_to`}
                          type="time"
                          value={dayData.to}
                          onChange={(e) => handleTimeChange(day.key, "to", e.target.value)}
                          required
                        />
                      </div>
                    )}
                  </div>
                );
              })}

              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                  <Icon icon="lucide:alert-circle" className="h-4 w-4" />
                  <span>{error}</span>
                </div>
              )}
            </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Go Back
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saving}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SetAvailabilityModal;

